import { CapacitorHttp } from '@capacitor/core';

import { APP_MEDIA_PATH_PREFIX, APP_QUERY_PARAMS, MEDIA_CDN_HOSTNAME } from '../../../common/utils/urlConstants';
import { parseUrl } from '../../../common/utils/urlUtil';
import { MimeType } from '../../../common/media/mimeConstants';
import fetchImageDataFromUrl from '../../components/images/imageColorExtraction/fetchImageDataFromUrl';
import { getCurrentUserToken } from '../../auth/authService';
import { getAuthenticatedHeaders, shouldFetchWithAuthentication } from '../../auth/authUtils';
import { getMimeTypeFromImageUrl } from './imageUtils';

/**
 * Fetch image bytes through the browser fetch API when standard web CORS/auth handling is available.
 */
const fetchImageResponse = (url: string, token?: string): Promise<Response> =>
    fetch(url, {
        headers: getAuthenticatedHeaders(token),
    });

/**
 * App-hosted v2 media URLs require element-scoped permission checks, so the first browser request must be authenticated.
 * CDN-hosted media stays public-first to avoid the CORS regression fixed in PR #20897.
 */
const shouldFetchWithAuthenticationImmediately = (url: string): boolean => {
    const parsedUrl = parseUrl(url);
    if (!parsedUrl) return false;

    const isAppMediaUrl =
        parsedUrl.hostname !== MEDIA_CDN_HOSTNAME && parsedUrl.pathname.startsWith(APP_MEDIA_PATH_PREFIX);
    if (!isAppMediaUrl) return false;

    return (
        parsedUrl.searchParams.get(APP_QUERY_PARAMS.VERSION) === '2' &&
        !!parsedUrl.searchParams.get(APP_QUERY_PARAMS.ELEMENT_ID)
    );
};

/**
 * Download image bytes for editing or background removal.
 * Try public image URLs without auth first, except for app-hosted v2 media URLs that require auth for permission checks.
 */
export const fetchImageBlobFromUrl = async (url: string): Promise<Blob> => {
    const token = getCurrentUserToken();
    const canSendAuthentication = !!token && shouldFetchWithAuthentication(url);
    const shouldSendAuthenticationImmediately = canSendAuthentication && shouldFetchWithAuthenticationImmediately(url);
    const response = await fetchImageResponse(url, shouldSendAuthenticationImmediately ? token : undefined);

    if (response.ok) return response.blob();
    if (!canSendAuthentication || shouldSendAuthenticationImmediately) {
        throw new Error(`Failed to fetch image (${response.status})`);
    }

    const authenticatedResponse = await fetchImageResponse(url, token);

    if (authenticatedResponse.ok) return authenticatedResponse.blob();

    throw new Error(`Failed to fetch image (${authenticatedResponse.status})`);
};

/**
 * Download image bytes through Capacitor for editor flows that need a local object URL on mobile.
 */
export const fetchImageUsingCapacitorHTTP = async (
    imageUrl: string,
    imageType?: string,
): Promise<{ objectUrl: string }> => {
    try {
        const blob = await fetchImageBlobFromUrl(imageUrl);
        return { objectUrl: URL.createObjectURL(blob) };
    } catch {
        const token = getCurrentUserToken();
        const shouldSendAuthentication = !!token && shouldFetchWithAuthentication(imageUrl);
        const response = await CapacitorHttp.get({
            url: imageUrl,
            responseType: 'arraybuffer',
            headers: getAuthenticatedHeaders(shouldSendAuthentication ? token : undefined),
        });

        if (response.status < 200 || response.status >= 300) {
            throw new Error(`Failed to fetch image (${response.status})`);
        }

        const base64Data = response.data;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);

        const responseMimeType = response.headers?.['content-type'];
        const resolvedImageType = imageType || responseMimeType || getMimeTypeFromImageUrl(imageUrl, MimeType.JPEG);

        const blob = new Blob([byteArray], { type: resolvedImageType });
        const objectUrl = URL.createObjectURL(blob);

        return { objectUrl };
    }
};

// Fetch image data using through capacitor for flows that require the image data directly
export const fetchImageDataUsingCapacitorHTTP = async (imageUrl: string, imageType?: string): Promise<ImageData> => {
    const { objectUrl } = await fetchImageUsingCapacitorHTTP(imageUrl, imageType);

    try {
        return await fetchImageDataFromUrl(objectUrl);
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};
